import type { AuthMethod } from "@defuse-protocol/internal-utils"
import { QuoteRequest } from "@defuse-protocol/one-click-sdk-typescript"
import { getWithdrawQuote as getWithdrawQuoteApi } from "@src/components/DefuseSDK/features/machines/1cs"
import type { BaseTokenInfo } from "@src/components/DefuseSDK/types/base"
import { getWithdrawDestinationAsset } from "@src/components/DefuseSDK/utils/oneClickAssetRouting"
import { adjustDecimals } from "@src/components/DefuseSDK/utils/tokenUtils"
import { logStablecoinWithdrawQuoteFailed } from "@src/utils/logCustom"
import { logger } from "@src/utils/logger"
import { type ActorRef, type Snapshot, fromCallback } from "xstate"
import { getWithdrawQuoteWithDynamicSlippage } from "../utils/dynamicSlippage"
import {
  serializeWithdrawQuoteRecipient,
  type WithdrawQuoteRecipient,
} from "../utils/withdrawRecipient"

export type WithdrawQuote1csInput = {
  tokenIn: BaseTokenInfo
  tokenOut: BaseTokenInfo
  amount: { amount: bigint; decimals: number }
  swapType: QuoteRequest.swapType
  slippageBasisPoints: number
  slippageExplicitlySet?: boolean
  defuseUserId: string
  deadline: string
  userAddress: string
  userChainType: AuthMethod
  recipient: WithdrawQuoteRecipient
  destinationMemo?: string
  virtualChainRecipient?: string
  destinationChainName?: string
  isConfidential?: boolean
}

export type Events =
  | {
      type: "NEW_QUOTE_INPUT"
      params: WithdrawQuote1csInput
    }
  | {
      type: "PAUSE"
    }

type EmittedEvents = {
  type: "NEW_WITHDRAW_1CS_QUOTE"
  params: {
    quoteInput: WithdrawQuote1csInput
    result:
      | {
          ok: {
            quote: {
              amountIn: string
              amountOut: string
              deadline?: string
              timeEstimate?: number
            }
            appFee: [string, bigint][]
          }
        }
      | {
          err: string
          correlationId?: string
          originalRequest?: QuoteRequest | undefined
        }
    tokenInAssetId: string
    tokenOutAssetId: string
  }
}

export type ParentEvents = EmittedEvents
type ParentActor = ActorRef<Snapshot<unknown>, ParentEvents>

type Input = {
  parentRef: ParentActor
}

export const backgroundWithdraw1csQuoterMachine = fromCallback<
  Events,
  Input,
  EmittedEvents
>(({ receive, input, emit }) => {
  let lastSetRequestId = 0

  function executeQuote(quoteInput: WithdrawQuote1csInput) {
    const requestId = ++lastSetRequestId

    getWithdrawQuote(
      quoteInput,
      (result, tokenInAssetId, tokenOutAssetId, slippageBasisPoints) => {
        if (requestId !== lastSetRequestId) return

        const eventPayload = {
          type: "NEW_WITHDRAW_1CS_QUOTE" as const,
          params: {
            quoteInput: { ...quoteInput, slippageBasisPoints },
            result,
            tokenInAssetId,
            tokenOutAssetId,
          },
        }

        input.parentRef.send(eventPayload)
        emit(eventPayload)
      }
    )
  }

  receive((event) => {
    const eventType = event.type
    switch (eventType) {
      case "PAUSE":
        lastSetRequestId++
        return
      case "NEW_QUOTE_INPUT": {
        executeQuote(event.params)
        break
      }
      default:
        eventType satisfies never
        logger.warn("Unhandled event type", { eventType })
    }
  })

  return () => lastSetRequestId++
})

async function getWithdrawQuote(
  quoteInput: WithdrawQuote1csInput,
  onResult: (
    result:
      | {
          ok: {
            quote: {
              amountIn: string
              amountOut: string
              timeEstimate?: number
            }
            appFee: [string, bigint][]
          }
        }
      | { err: string },
    tokenInAssetId: string,
    tokenOutAssetId: string,
    slippageBasisPoints: number
  ) => void
): Promise<void> {
  const tokenInAssetId = quoteInput.tokenIn.defuseAssetId
  const tokenOutAssetId =
    quoteInput.destinationChainName === "bitcoin"
      ? getWithdrawDestinationAsset(quoteInput.tokenOut.defuseAssetId)
      : quoteInput.tokenOut.defuseAssetId

  try {
    const request = {
      dry: true,
      originAsset: tokenInAssetId,
      destinationAsset: tokenOutAssetId,
      amount: (quoteInput.swapType === QuoteRequest.swapType.EXACT_OUTPUT
        ? adjustDecimals(
            quoteInput.amount.amount,
            quoteInput.amount.decimals,
            quoteInput.tokenOut.decimals
          )
        : quoteInput.amount.amount
      ).toString(),
      deadline: quoteInput.deadline,
      userAddress: quoteInput.userAddress,
      authMethod: quoteInput.userChainType,
      swapType: quoteInput.swapType,
      ...serializeWithdrawQuoteRecipient(
        quoteInput.recipient,
        quoteInput.isConfidential
      ),
      ...(quoteInput.destinationMemo
        ? { destinationMemo: quoteInput.destinationMemo }
        : {}),
      ...(quoteInput.virtualChainRecipient
        ? { virtualChainRecipient: quoteInput.virtualChainRecipient }
        : {}),
      ...(quoteInput.destinationChainName
        ? { destinationChainName: quoteInput.destinationChainName }
        : {}),
      ...(quoteInput.isConfidential
        ? { isConfidential: quoteInput.isConfidential }
        : {}),
    }
    const { result, slippageBasisPoints } =
      await getWithdrawQuoteWithDynamicSlippage({
        tokenIn: quoteInput.tokenIn,
        tokenOut: quoteInput.tokenOut,
        maxSlippageBasisPoints: quoteInput.slippageBasisPoints,
        slippageExplicitlySet: quoteInput.slippageExplicitlySet,
        getQuote: (candidate) =>
          getWithdrawQuoteApi({
            ...request,
            slippageTolerance: Math.round(candidate / 100),
          }),
        onAttemptFailed: (attempt) => {
          if (!attempt.isStablecoinWithdrawal) return
          logStablecoinWithdrawQuoteFailed({
            tokenIn: quoteInput.tokenIn,
            tokenOut: quoteInput.tokenOut,
            slippageBasisPoints: attempt.slippageBasisPoints,
            message: String(attempt.result.err),
            correlationId: attempt.result.correlationId,
          })
        },
      })

    onResult(result, tokenInAssetId, tokenOutAssetId, slippageBasisPoints)
  } catch (error) {
    logger.error("1cs withdraw quote request failed", {
      error,
      tokenInAssetId,
      tokenOutAssetId,
    })
    onResult(
      { err: error instanceof Error ? error.message : "Quote request failed" },
      tokenInAssetId,
      tokenOutAssetId,
      quoteInput.slippageBasisPoints
    )
  }
}
